import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import LoadingButtonWrapper from '~/components/LoadingButtonWrapper';
import useResponsive from '~/hooks/useResponsive';

import { getAppLinks } from '../../../utils/helpers';
import AppLink from '../../SelfManagedHosting/components/AppLink';
import LetterAvatar from './LetterAvatar';

interface SelectableListCardProps {
  app: string;
  title: string;
  description: string;
  shortDescription: string | undefined;
  icon: string;
  buttonText: string;
  onSelect: (selectedApp: string) => void;
  testId?: string;
  showCurrentServerImageOrApp?: boolean;
  quickStartUrl?: string;
  docsUrl?: string;
  communityUrl?: string;
  forumUrl?: string;
  getStartedUrl?: string;
}

const SelectableListCard = ({
  app,
  title,
  description,
  shortDescription,
  icon,
  buttonText,
  onSelect,
  testId = 'self-managed-card',
  showCurrentServerImageOrApp = false,
  docsUrl = '',
  communityUrl = '',
  forumUrl = '',
  getStartedUrl = '',
}: SelectableListCardProps) => {
  const { t } = useTranslation('hosting', { keyPrefix: 'hostingNova' });
  const [showExpanded, setShowExpanded] = useState(false);
  const selectedKeyRef = useRef<string | null>(null);
  const { isMobile } = useResponsive();

  const handleSelect = () => {
    selectedKeyRef.current = app;
    onSelect(app);
  };

  const handleLearnMore = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowExpanded(true);
  };

  const APP_URLS = getAppLinks(docsUrl, communityUrl, forumUrl, getStartedUrl);
  const hasExtraLinks = APP_URLS.some(({ url }) => !!url);

  const learnMoreLink = !showExpanded && (
    <Link
      href="#"
      onClick={handleLearnMore}
      data-testid={`learn-more-${title}`}
      underline="none"
      sx={{
        pt: 1,
        textDecoration: 'none',
      }}
    >
      {t('selfManagedApps.learnMore')}
    </Link>
  );

  const linksExpanded = showExpanded && hasExtraLinks && (
    <Stack spacing={4} sx={{ pt: 1 }} direction="row">
      {APP_URLS.filter(({ url }) => !!url).map(({ url, urlText }) => (
        <AppLink
          key={urlText}
          url={url ?? ''}
          urlText={t(`selfManagedApps.${urlText}`)}
          testId={`${urlText}-link`}
        />
      ))}
    </Stack>
  );

  const displayDescription = showExpanded ? description : shortDescription;
  const cardSx = showExpanded || isMobile() ? {} : { height: 150 };
  const isSvgOrPng = typeof icon === 'string' && /\.(svg|png)$/i.test(icon);
  const avatar = isSvgOrPng ? (
    <Avatar
      alt={`${title} logo`}
      src={icon}
      sx={{
        width: 40,
        height: 40,
        '& img': {
          objectFit: 'contain',
        },
      }}
    />
  ) : (
    <LetterAvatar letters={icon} width={40} height={40} />
  );

  const chip = showCurrentServerImageOrApp && (
    <Chip
      label={t('selfManagedApps.current')}
      color={'secondary'}
      size="small"
      variant={'outlined'}
      data-testid={`${testId}-current-label`}
    />
  );

  const button = (
    <LoadingButtonWrapper
      onClick={handleSelect}
      fullWidth={isMobile()}
      data-testid={`${testId}-action`}
    >
      {buttonText}
    </LoadingButtonWrapper>
  );

  return (
    <Card variant="contained" data-testid={testId} sx={cardSx}>
      <CardContent
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          '&:last-child': { paddingBottom: 2 },
        }}
      >
        {isMobile() ? (
          <Stack spacing={1} sx={{ width: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {avatar}
              <Typography variant="h3">{title}</Typography>
              {chip}
            </Stack>
            <Typography variant="body1" sx={{ pt: 0.5 }}>
              {displayDescription}
            </Typography>
            <Stack spacing={2}>
              {learnMoreLink}
              {linksExpanded}
              {button}
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
            <Box sx={{ mr: 2, flexShrink: 0 }}>{avatar}</Box>
            <Stack flexDirection="column" sx={{ flex: 1, minWidth: 0, mr: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h3">{title}</Typography>
                {chip}
              </Stack>
              <Typography variant="body1" sx={{ pt: 1 }}>
                {displayDescription}
              </Typography>
              {learnMoreLink}
              {linksExpanded}
            </Stack>
            <Box sx={{ flexShrink: 0 }}>{button}</Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default SelectableListCard;
